import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { INQUIRY_SOURCE_LABELS, INQUIRY_STATUS_LABELS, INQUIRY_STATUSES, type Inquiry, type InquiryAttachment, type InquiryLogEntry, type InquiryStatus, type UpdateInquiryInput } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useSession } from '../session';
import { CollapsibleCard } from '../components/CollapsibleCard';
import { FollowUpPanel } from '../modules/crm';
import { StatusBadge } from '../components/StatusBadge';
import { UpgradeDialog } from '../components/UpgradeDialog';
import { displayFields, useInquiryTypes } from '../inquiryTypes';
import { InquiryContactCreateButton, InquiryContactPanel } from '../modules/contacts';
import { RelatedOffers } from '../modules/offers';
import { InquiryLeavePanel } from '../modules/hr';
import { InquiryOrderButton } from '../modules/orders';

interface UserOption {
  id: string;
  name: string;
  status: 'active' | 'invited' | 'deactivated';
}

/** 4,2 MB statt 4404019 Bytes */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Byte`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}

const dateTime = new Intl.DateTimeFormat('de-DE', { dateStyle: 'full', timeStyle: 'short' });
const dateOnly = new Intl.DateTimeFormat('de-DE', { dateStyle: 'full' });
const logTime = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Berlin' });

export function InquiryDetailPage() {
  const { id = '' } = useParams();
  const { can, hasModule } = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const canWrite = can('inquiries.write');
  const types = useInquiryTypes();

  const inquiry = useQuery({ queryKey: ['inquiry', id], queryFn: async () => (await api<{ data: Inquiry }>('GET', `/inquiries/${id}`)).data });
  const users = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api<{ data: UserOption[] }>('GET', '/users')).data,
    enabled: canWrite && hasModule('users') && can('users.read'),
  });

  const attachments = useQuery({
    queryKey: ['inquiry', id, 'attachments'],
    queryFn: async () => (await api<{ data: InquiryAttachment[] }>('GET', `/inquiries/${id}/attachments`)).data,
  });

  const log = useQuery({
    queryKey: ['inquiry', id, 'log'],
    queryFn: async () => (await api<{ data: InquiryLogEntry[] }>('GET', `/inquiries/${id}/log`)).data,
  });

  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const offersButton = useRef<HTMLButtonElement>(null);
  // Wer das Modul Angebote nicht im Paket hat, bekommt beim Klick den Hinweis aufs Upgrade (KON-103)
  const offersLicensed = hasModule('offers');
  const canOffer = offersLicensed && can('offers.write');

  const [note, setNote] = useState('');
  useEffect(() => setNote(inquiry.data?.internalNote ?? ''), [inquiry.data?.internalNote]);

  const save = useMutation({
    mutationFn: (input: UpdateInquiryInput) => api<{ data: Inquiry }>('PATCH', `/inquiries/${id}`, input),
    onSuccess: (res) => {
      client.setQueryData(['inquiry', id], res.data);
      client.invalidateQueries({ queryKey: ['inquiries'] });
      client.invalidateQueries({ queryKey: ['inquiry', id, 'log'] });
    },
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/inquiries/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['inquiries'] });
      navigate('/anfragen', { replace: true });
    },
  });

  if (inquiry.isLoading) return <p className="muted">Wird geladen …</p>;
  if (inquiry.isError || !inquiry.data) {
    return (
      <div className="card narrow">
        <h1>Anfrage nicht gefunden</h1>
        <Link to="/anfragen">Zur Übersicht</Link>
      </div>
    );
  }
  const i = inquiry.data;
  // Ein Freistellungsantrag (KON-121) ist keine Kundenanfrage: kein Kontaktbuch, kein Angebot
  const isLeave = i.type === 'freistellung';
  const saveError = save.error instanceof ApiError ? save.error.message : null;

  return (
    <>
      <p><Link to="/anfragen" className="back-link">← Alle Anfragen</Link></p>
      <div className="page-head">
        <h1>{i.reference}</h1>
        <StatusBadge status={i.status} />
        <span className="muted">{types.label(i.type)}</span>
      </div>
      <p className="muted">Eingegangen am {dateTime.format(new Date(i.createdAt))} über {INQUIRY_SOURCE_LABELS[i.source] ?? i.source}.</p>

      <div className="detail-grid">
        <section className="card">
          <div className="card-head">
            <h2>Kontakt</h2>
            {!isLeave && <InquiryContactCreateButton inquiry={i} />}
          </div>
          <dl className="facts">
            <dt>Name</dt><dd>{i.contact.name}</dd>
            {i.contact.company && (<><dt>Firma</dt><dd>{i.contact.company}</dd></>)}
            <dt>E-Mail</dt><dd><a href={`mailto:${i.contact.email}?subject=${encodeURIComponent(`Ihre Anfrage ${i.reference}`)}`}>{i.contact.email}</a></dd>
            {i.contact.phone && (<><dt>Telefon</dt><dd><a href={`tel:${i.contact.phone.replace(/[^+\d]/g, '')}`}>{i.contact.phone}</a></dd></>)}
            {i.contact.customerType && (<><dt>Kundenart</dt><dd>{i.contact.customerType === 'business' ? 'Firma' : 'Privat'}</dd></>)}
          </dl>

          {!isLeave && <InquiryContactPanel inquiry={i} />}

          <h2>Anliegen</h2>
          <dl className="facts">
            {i.preferredDate && (<><dt>Wunschtermin</dt><dd>{dateOnly.format(new Date(i.preferredDate))}</dd></>)}
            {displayFields(types.get(i.type), i.fields).map((f) => (
              <Fragment key={f.key}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </Fragment>
            ))}
          </dl>
          {i.message && <p className="message">{i.message}</p>}

          {attachments.isError && <p className="muted">Die Dateien konnten nicht geladen werden.</p>}
          {attachments.data && attachments.data.length > 0 && (
            <>
              <h2>Dateien</h2>
              <ul className="attachments">
                {attachments.data.map((a) => (
                  <li key={a.id}>
                    <a href={`/api/v1/inquiries/${i.id}/attachments/${a.id}/download`} download={a.fileName}>{a.fileName}</a>
                    <span className="muted"> ({formatSize(a.sizeBytes)})</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="card">
          <h2>Bearbeitung</h2>
          {saveError && <div className="alert alert--error">{saveError}</div>}
          <label className="field">
            <span>Status</span>
            <select className="input" value={i.status} disabled={!canWrite || save.isPending} onChange={(e) => save.mutate({ status: e.target.value as InquiryStatus })}>
              {INQUIRY_STATUSES.map((s) => (
                <option key={s} value={s}>{INQUIRY_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </label>

          {users.data && (
            <label className="field">
              <span>Zuständig</span>
              <select className="input" value={i.assignedUserId ?? ''} disabled={save.isPending} onChange={(e) => save.mutate({ assignedUserId: e.target.value || null })}>
                <option value="">Niemand</option>
                {users.data.filter((u) => u.status === 'active').map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </label>
          )}

          <label className="field">
            <span>Interne Notiz</span>
            <textarea className="input textarea" value={note} disabled={!canWrite} onChange={(e) => setNote(e.target.value)} maxLength={5000} placeholder="Nur für Ihr Team sichtbar" />
          </label>
          {canWrite && (
            <button className="btn btn--primary" type="button" disabled={save.isPending || note === (i.internalNote ?? '')} onClick={() => save.mutate({ internalNote: note || null })}>
              Notiz speichern
            </button>
          )}

          {(canWrite || can('inquiries.delete')) && (
            <div className="danger-zone">
              {canWrite && !isLeave && <InquiryOrderButton inquiryId={i.id} />}
              {canWrite && !isLeave && (
                <button
                  ref={offersButton}
                  className={`btn btn--secondary btn--sm${offersLicensed ? '' : ' btn--locked'}`}
                  type="button"
                  disabled={offersLicensed && !canOffer}
                  title={offersLicensed && !canOffer ? 'Ihre Rolle darf keine Angebote anlegen.' : offersLicensed ? undefined : 'Gehört nicht zu Ihrem Paket'}
                  onClick={() => (canOffer ? navigate(`/angebote/neu?anfrage=${i.id}`) : setUpgradeOpen(true))}
                >
                  Angebot erstellen
                </button>
              )}
              {canWrite && !isLeave && i.status !== 'rejected' && (
                <button
                  className="btn btn--secondary btn--sm"
                  type="button"
                  disabled={save.isPending}
                  title="Die Anfrage bekommt den Stand „Abgelehnt“ und verschwindet aus der Arbeitsliste. Sie bleibt gespeichert."
                  onClick={() => {
                    if (window.confirm(`Anfrage ${i.reference} ablehnen? Sie bekommt den Stand „Abgelehnt“ und bleibt gespeichert.`)) save.mutate({ status: 'rejected' });
                  }}
                >
                  Ablehnen
                </button>
              )}
              {can('inquiries.delete') && (
                <button
                  className="btn btn--danger btn--sm"
                  type="button"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(`Anfrage ${i.reference} wirklich löschen?`)) remove.mutate();
                  }}
                >
                  Anfrage löschen
                </button>
              )}
            </div>
          )}
        </section>
      </div>

      <div className="detail-stack">
      {isLeave && <InquiryLeavePanel inquiryId={i.id} />}

      {!isLeave && offersLicensed && can('offers.read') && <RelatedOffers inquiryId={i.id} />}

      <FollowUpPanel subjectKind="inquiry" subjectId={i.id} />

      <CollapsibleCard title="Ablaufprotokoll" storageKey="inquiry-log">
        <p className="muted small">Wann die Anfrage eingegangen ist und wer wann den Status geändert hat.</p>
        {log.isLoading && <p className="muted">Wird geladen …</p>}
        {log.isError && <p className="muted">Das Ablaufprotokoll konnte nicht geladen werden.</p>}
        {log.data && (
          <ul className="history-list">
            {log.data.map((e) => (
              <li key={e.id}>
                <span className="history-list__when">{logTime.format(new Date(e.at))} Uhr</span>
                <span>
                  {e.kind === 'received' ? (
                    <>Eingang über {INQUIRY_SOURCE_LABELS[e.source ?? ''] ?? e.source ?? 'unbekannten Weg'}</>
                  ) : (
                    <>
                      Status <StatusBadge status={e.from!} />
                      <span className="arrow" aria-label="auf">→</span>
                      <StatusBadge status={e.to!} />
                    </>
                  )}
                  {e.actorName && <span className="muted"> von {e.actorName}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CollapsibleCard>
      </div>

      <UpgradeDialog
        module="offers"
        open={upgradeOpen}
        onClose={() => {
          setUpgradeOpen(false);
          offersButton.current?.focus();
        }}
      />
    </>
  );
}
